import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LanguageService } from '../../shared/services/language.service';
import { TenantsService, TenantDto } from '../../shared/services/tenants.service';
import { ManageTenantDialogComponent } from '../manage-tenant-dialog/manage-tenant-dialog.component';
import { TenantConfirmDialogComponent, TenantConfirmDialogData } from '../tenant-confirm-dialog/tenant-confirm-dialog.component';
import { InfoBarWithStatusService } from '../../shared/services/info-bar-with-status.service';

@Component({
  standalone: false,
  selector: 'app-tenants-management',
  templateUrl: './tenants-management.component.html',
  styleUrls: ['./tenants-management.component.css']
})
export class TenantsManagementComponent implements OnInit {

  variables: any;
  tenants: TenantDto[] = [];
  isLoading = true;
  loadError = false;
  errorText = '';
  colorWhite = '#fff';
  readonly displayedColumns: string[] = ['id', 'name', 'logo', 'mainColor', 'secondaryColor', 'useSso', 'active', 'actions'];

  constructor(
    private languageService: LanguageService,
    private tenantsService: TenantsService,
    private dialog: MatDialog,
    private infoBar: InfoBarWithStatusService
  ) { }

  ngOnInit(): void {
    this.languageService.translations$.subscribe((vars: any) => {
      this.variables = vars;
    });
    this.reload();
  }

  reload(): void {
    this.isLoading = true;
    this.loadError = false;
    this.errorText = '';
    this.tenantsService.list(false).subscribe({
      next: (rows) => {
        this.tenants = rows ?? [];
        this.isLoading = false;
      },
      error: () => {
        this.loadError = true;
        this.isLoading = false;
        this.errorText = this.variables?.detectedError ?? 'Error';
      }
    });
  }

  openCreate(): void {
    const ref = this.dialog.open(ManageTenantDialogComponent, {
      data: { title: this.variables?.addTenant, mode: 'create' }
    });
    ref.afterClosed().subscribe((res: { refresh?: boolean }) => {
      if (res?.refresh) {
        this.infoBar.showSnackbar(this.variables?.tenantSaveSuccess, ['successful-snackbar'], 4000);
        this.reload();
      }
    });
  }

  openEdit(t: TenantDto): void {
    const ref = this.dialog.open(ManageTenantDialogComponent, {
      data: { title: this.variables?.editTenant, mode: 'edit', tenant: { ...t } }
    });
    ref.afterClosed().subscribe((res: { refresh?: boolean }) => {
      if (res?.refresh) {
        this.infoBar.showSnackbar(this.variables?.tenantSaveSuccess, ['successful-snackbar'], 4000);
        this.reload();
      }
    });
  }

  /** Read-only view of the tenant — same form as edit, but disabled and no Save button. */
  openView(t: TenantDto): void {
    this.dialog.open(ManageTenantDialogComponent, {
      data: { title: this.variables?.viewTenant, mode: 'view', tenant: { ...t } }
    });
  }

  /** Confirms via dialog, then issues DELETE /tenants/{id} and surfaces backend errors. */
  confirmDelete(t: TenantDto): void {
    const data: TenantConfirmDialogData = {
      title: this.fmt(this.variables?.tenantDeleteConfirmTitle, t),
      message: '',
      detail: this.variables?.tenantDeleteConfirmDetail,
      confirmLabel: this.variables?.confirmAction || 'Confirm',
      cancelLabel: this.variables?.cancelAction || 'Cancel',
      dangerous: true
    };
    this.dialog.open(TenantConfirmDialogComponent, { data })
      .afterClosed().subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.tenantsService.delete(t.id).subscribe({
          next: () => {
            this.infoBar.showSnackbar(this.variables?.tenantDeleteSuccess, ['successful-snackbar'], 4000);
            this.reload();
          },
          error: (err) => {
            // Backend returns 400 with a specific message when the tenant has data.
            const isCascadeError = err?.status === 400;
            const msg = isCascadeError
              ? (this.variables?.tenantDeleteHasDataError || err?.error?.message || this.variables?.detectedError)
              : (this.variables?.detectedError || 'Error');
            this.infoBar.showSnackbar(msg, ['error-snackbar'], 6000);
          }
        });
      });
  }

  /** Activate / deactivate share the same backend call (PUT with active flag). */
  confirmToggleActive(t: TenantDto): void {
    const willActivate = !t.active;
    const titleKey = willActivate ? 'tenantActivateConfirmTitle' : 'tenantDeactivateConfirmTitle';
    const detail = willActivate ? undefined : this.fmt(this.variables?.tenantDeactivateConfirmDetail, t);
    const data: TenantConfirmDialogData = {
      title: this.fmt(this.variables?.[titleKey], t),
      message: '',
      detail,
      confirmLabel: this.variables?.confirmAction || 'Confirm',
      cancelLabel: this.variables?.cancelAction || 'Cancel',
      dangerous: !willActivate
    };
    this.dialog.open(TenantConfirmDialogComponent, { data })
      .afterClosed().subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.tenantsService.update(t.id, {
          name: t.name,
          active: willActivate,
          logo: t.logo,
          mainColor: t.mainColor,
          secondaryColor: t.secondaryColor,
          useSso: t.useSso
        }).subscribe({
          next: () => {
            this.infoBar.showSnackbar(this.variables?.tenantStatusChangeSuccess, ['successful-snackbar'], 4000);
            this.reload();
          },
          error: () => {
            this.infoBar.showSnackbar(this.variables?.detectedError || 'Error', ['error-snackbar'], 5000);
          }
        });
      });
  }

  /** Substitutes {name} in a translation template with the tenant's display name. */
  private fmt(template: string | undefined, t: TenantDto): string {
    if (!template) return '';
    return template.replace('{name}', t.name || t.id);
  }
}
